import { getMyGroups, messageKey } from './api.js'
import { el } from './dom.js'
import { t } from './labels.js'

// Il selettore dei gruppi: la schermata che si vede quando la dashboard si apre SENZA sapere quale
// gruppo configurare.
//
// Perché esiste (sessione 84). Fino a ieri quella condizione era un vicolo cieco: la Mini App poteva
// solo dire «nessun gruppo selezionato, esegui /dashboard nel tuo gruppo e tocca il pulsante lì» —
// cioè rimandava l'utente indietro, in un percorso di quattro passaggi da rifare ogni volta. È il
// motivo per cui la dashboard NON poteva essere aperta da un pulsante di menu o da un'icona sulla
// schermata Home: senza un id in arrivo dall'esterno non sapeva cosa mostrare. Ora lo chiede.
//
// La scelta ricarica la pagina con `?chat=<id>` invece di montare la dashboard qui. È deliberato: il
// flusso di avvio resta ESATTAMENTE quello di prima — stesso percorso, stessi controlli, stessa
// riautorizzazione lato server — e questo modulo non può romperlo. Un reload locale è impercettibile,
// e vale il rischio che non si corre.
//
// ⚠️ CHI ARRIVA QUI NON È SOLO L'OPERATORE (sessione 85). Il pulsante «Dashboard» è il default di
// TUTTE le chat private, quindi lo vedono anche i ~978 membri dei gruppi moderati, che amministratori
// non sono: per loro questa schermata è il primo — e forse unico — contatto con TurBOT. I testi che
// usa devono reggere quella lettura, non solo quella di chi è venuto a configurare.

/**
 * Disegna il selettore dentro `root`. Risolve quando la schermata è a posto; la scelta dell'utente
 * ricarica la pagina, quindi da qui in poi non torna nulla.
 */
export async function renderGroupPicker(root: HTMLElement): Promise<void> {
  root.replaceChildren(el('p', { class: 'pick-loading' }, t('ui-pick-loading')))

  let risposta: { groups: { chatId: string, title: string, type: string }[], truncated: boolean, rosterOnly?: boolean }
  try {
    risposta = await getMyGroups()
  }
  catch (err) {
    // La causa si DICE, e determina se ha senso offrire «Riprova» (sessione 85, rilievo N2). Prima
    // qui c'era un catch cieco: un token scaduto (401) e un rate limit (429) mostravano entrambi il
    // messaggio generico più un pulsante che, sul 429, ritenta DENTRO il limite. La mappa è la stessa
    // che usa l'orchestratore — un fatto in un posto solo.
    const chiave = messageKey(err, 'ui-pick-failed')
    // Riprovare aiuta quando la causa è passeggera (rete, 503, un errore inatteso). Non aiuta su un
    // token scaduto — lì si chiude e si riapre — né su un rate limit, dove il pulsante peggiora le cose.
    const riprovabile = chiave !== 'ui-err-unauthorized' && chiave !== 'ui-err-rate-limited'
    root.replaceChildren(
      el('p', { class: 'pick-empty' }, t(chiave)),
      ...(riprovabile ? [bottoneRicarica()] : []),
    )
    return
  }

  // Il proprietario (ADR 0007) vede le chat REGISTRATE, non tutte: lo si dice, e gli si offre di aprire un
  // gruppo per id — l'unica via verso un gruppo che il roster non ha mai visto (occhio freddo, giro 4).
  if (risposta.rosterOnly === true && risposta.groups.length === 0) {
    root.replaceChildren(...apriPerId())
    return
  }
  if (risposta.groups.length === 0) {
    root.replaceChildren(...schermataVuota())
    return
  }

  const lista = el('div', { class: 'sec-group pick-list' })
  for (const g of risposta.groups) {
    const riga = el(
      'button',
      { 'class': 'row row-tap pick-row', 'type': 'button', 'data-chat': g.chatId },
      el('span', { class: 'label' }, g.title),
      el('span', { class: 'value' }, '›'),
    )
    riga.addEventListener('click', () => {
      // `location.search` riavvia il caricamento con l'id scelto: da lì in poi è il percorso di sempre.
      location.search = `?chat=${encodeURIComponent(g.chatId)}`
    })
    lista.append(riga)
  }

  const pezzi: (Node | string)[] = [el('h2', { class: 'pick-title' }, t('ui-pick-title')), lista]
  // Se l'elenco è parziale lo si DICE: un elenco incompleto che si presenta come completo manda
  // l'utente a cercare un gruppo che c'è e non si vede. Il server alza questo flag quando una verifica
  // non è riuscita (Telegram in affanno): l'azione di chi legge è riaprire. Il dettaglio sta nel log.
  if (risposta.truncated)
    pezzi.push(el('p', { class: 'pick-note' }, t('ui-pick-partial')))
  if (risposta.rosterOnly === true)
    pezzi.push(...apriPerId())
  root.replaceChildren(...pezzi)
}

/**
 * L'id di un gruppo scritto a mano, o null. Un gruppo o supergruppo ha un id NEGATIVO di sole cifre; il
 * server lo rivalida comunque (schema della richiesta) e lo riautorizza: qui si evita solo un viaggio inutile.
 */
export function idGruppoDa(testo: string): string | null {
  const id = testo.trim()
  return /^-\d{5,19}$/.test(id) ? id : null
}

// La nota e il campo «apri per id» del proprietario: stessa strada della scelta da elenco (`?chat=<id>`).
function apriPerId(): Node[] {
  const campo = el('input', { 'class': 'lc-input', 'type': 'text', 'inputmode': 'text', 'aria-label': t('ui-pick-owner-id'), 'placeholder': t('ui-pick-owner-id') }) as HTMLInputElement
  const errore = el('p', { class: 'pick-note' }, '')
  const apri = el('button', { class: 'lc-btn', type: 'button' }, t('ui-pick-owner-open'))
  apri.addEventListener('click', () => {
    const id = idGruppoDa(campo.value)
    if (id === null) {
      errore.textContent = t('ui-pick-owner-id-invalid')
      return
    }
    location.search = `?chat=${encodeURIComponent(id)}`
  })
  return [el('p', { class: 'pick-note' }, t('ui-pick-owner-note')), el('div', { class: 'row' }, campo, apri), errore]
}

/**
 * La schermata di chi non amministra niente — impaginata, non raccontata (direttiva dell'operatore,
 * sessione 86: «venga presentato meglio e più leggibile»).
 *
 * Prima era UN paragrafo unico che diceva quattro cose di fila: la causa, cosa fare se amministri un
 * gruppo, cosa fare se sei un membro. Chi legge doveva estrarre da solo il proprio caso da un muro di
 * prosa — e questa schermata la aprono soprattutto i ~978 MEMBRI, per i quali «aggiungimi come
 * amministratore» non è nemmeno un'istruzione eseguibile. Ora i due casi sono due pannelli distinti,
 * con la stessa pelle delle altre sezioni della dashboard (`sec-head` + `sec-group`): l'occhio trova
 * il proprio ramo senza leggere l'altro, e i tre gesti di chi amministra sono tre righe numerate
 * invece di una frase con due virgole.
 *
 * Nessun testo è composto qui: ogni pezzo è una chiave a sé (i18n), perché è la UI a decidere l'aria
 * fra i blocchi — una traduzione non deve poter rompere l'impaginazione con la punteggiatura.
 */
function schermataVuota(): Node[] {
  const passo = (numero: number, chiave: string): HTMLElement => el(
    'div',
    { class: 'row' },
    el('span', { class: 'pick-step' }, String(numero)),
    el('span', { class: 'label' }, t(chiave)),
  )
  return [
    el('h2', { class: 'pick-title' }, t('ui-pick-empty-title')),
    el('p', { class: 'pick-empty' }, t('ui-pick-empty-lead')),
    el('div', { class: 'sec-head' }, el('h2', { class: 'sec-title' }, t('ui-pick-empty-admin'))),
    el(
      'div',
      { class: 'sec-group' },
      passo(1, 'ui-pick-empty-step-1'),
      passo(2, 'ui-pick-empty-step-2'),
      passo(3, 'ui-pick-empty-step-3'),
    ),
    el('div', { class: 'sec-head' }, el('h2', { class: 'sec-title' }, t('ui-pick-empty-member'))),
    el(
      'div',
      { class: 'sec-group' },
      el('div', { class: 'row' }, el('span', { class: 'label pick-member' }, t('ui-pick-empty-member-body'))),
    ),
  ]
}

function bottoneRicarica(): HTMLElement {
  const b = el('button', { class: 'lc-btn', type: 'button' }, t('ui-pick-retry'))
  b.addEventListener('click', () => {
    location.reload()
  })
  return b
}
